import React, { FC, useEffect } from 'react';
import { GetOrganisationQuery } from '../queries/GetOrganisation.generated';
import { useUpdateOrgLiquidityLimitsMutation } from './UpdateOrgLiquidityLimits.generated';
import { useGetOrganisationLiquidityLimitsQuery } from './GetOrganisationLiquidityLimits.generated';
import { useWrappedUrqlMutation } from 'toolbox';
import { LiquidityLimitForm } from './LiquidityLimitForm';
import CardWithAside from 'components/Card/CardWithAside';
import Button from 'components/Button';
import Input from 'components/Input';
import SubmitButton from 'components/SubmitButton/SubmitButton';
import ResetButton from 'components/ResetButton/ResetButton';
import { toast } from 'react-toastify';

interface IOrgLiquidityLimits {
  organisation_id: GetOrganisationQuery['organisations_by_pk']['id'];
}

export const OrgLiquidityLimits: FC<IOrgLiquidityLimits> = ({
  organisation_id,
}) => {
  const [query, refreshData] = useGetOrganisationLiquidityLimitsQuery({
    variables: { organisation_id },
    pause: !organisation_id,
  });
  const limits = query?.data?.organisations_by_pk?.organisation_liquidity_limits
    // .map((l) => {
    //   delete l.__typename;
    //   return l;
    // })
    .sort((a, b) => (a.notice_days_gte > b.notice_days_gte ? 1 : -1));

  const updateLimits = useWrappedUrqlMutation(
    useUpdateOrgLiquidityLimitsMutation,
    {
      onSuccess: (result) => {
        // toast.info('Update successful.');
        refreshData();
      },
      onError: (error) => {
        // toast.error(`Update failed.`);
        console.warn(error);
      },
    },
  );

  // this useEffect creates default limits if any are missing for this organisation
  useEffect(() => {
    if (!query.fetching && !query.error) {
      const objects = checkForAndCreateMissingLiquidityLimits(
        limits,
        organisation_id,
      );
      if (objects.length > 0) {
        console.log('Some liquidity limits missing, creating defaults...');
        updateLimits.dispatch({ objects });
      }
    }
  }, [query]);

  const submitHandler = (values) => {
    // console.log(values);
    updateLimits.dispatch({ objects: [{ ...values }] });
  };

  if (limits)
    return (
      <CardWithAside
        title="Liquidity Limits"
        aside="The dashboard will highlight a breach when there insufficient or excess funds held in a particular liquidity bracket, as determined by the organisation limits set here."
        // footer={<Button onClick={saveButtonHandler}>Save</Button>}
        children={limits?.map((limit, i) => (
          <LiquidityLimitForm.Wrapper
            key={i}
            initialValues={{
              organisation_id: limit?.organisation_id,
              notice_days_gte: limit?.notice_days_gte,
              notice_days_lt: limit?.notice_days_lt,
              lower_limit_pct: limit?.lower_limit_pct?.toString(),
              upper_limit_pct: limit?.upper_limit_pct?.toString(),
            }}
            onSubmit={submitHandler}
          >
            <div className="max-w-2xl px-3 pt-3 mb-3 border border-gray-200 rounded">
              <h2 className="pb-1 font-semibold text-gray-700">
                {limit?.notice_days_gte <= 0 && limit?.notice_days_lt === 1
                  ? `CALL accounts`
                  : `Accounts with ${limit?.notice_days_gte}`}
                {limit?.notice_days_lt === -1
                  ? `+ days notice`
                  : limit?.notice_days_lt === 1
                  ? ``
                  : ` to ${limit?.notice_days_lt - 1} days notice`}
              </h2>
              <div className="grid items-end grid-flow-col gap-2">
                <LiquidityLimitForm.Inputs.notice_days_gte />
                <LiquidityLimitForm.Inputs.notice_days_lt />
                <LiquidityLimitForm.Inputs.lower_limit_pct label="Minimum %" />
                <LiquidityLimitForm.Inputs.upper_limit_pct label="Maximum %" />
                <div className="flex items-end h-full mb-3 text-right">
                  <ResetButton>Reset</ResetButton>
                  <SubmitButton disableIfNoInputsTouched>Save</SubmitButton>
                </div>
              </div>
            </div>
          </LiquidityLimitForm.Wrapper>
        ))}
      />
    );

  return null;
};

const checkForAndCreateMissingLiquidityLimits = (limits, organisation_id) => {
  const objects = [];

  if (!limits?.some((l) => l?.notice_days_gte === 0 && l?.notice_days_lt === 1))
    objects.push({
      organisation_id,
      notice_days_gte: 0,
      notice_days_lt: 1,
      lower_limit_pct: 0,
      upper_limit_pct: 100,
    });

  if (!limits?.some((l) => l?.notice_days_gte === 1 && l?.notice_days_lt === 8))
    objects.push({
      organisation_id,
      notice_days_gte: 1,
      notice_days_lt: 8,
      lower_limit_pct: 0,
      upper_limit_pct: 100,
    });

  if (
    !limits?.some((l) => l?.notice_days_gte === 8 && l?.notice_days_lt === 36)
  )
    objects.push({
      organisation_id,
      notice_days_gte: 8,
      notice_days_lt: 36,
      lower_limit_pct: 0,
      upper_limit_pct: 100,
    });

  if (
    !limits?.some((l) => l?.notice_days_gte === 36 && l?.notice_days_lt === 96)
  )
    objects.push({
      organisation_id,
      notice_days_gte: 36,
      notice_days_lt: 96,
      lower_limit_pct: 0,
      upper_limit_pct: 100,
    });

  if (
    !limits?.some((l) => l?.notice_days_gte === 96 && l?.notice_days_lt === -1)
  )
    objects.push({
      organisation_id,
      notice_days_gte: 96,
      notice_days_lt: -1,
      lower_limit_pct: 0,
      upper_limit_pct: 100,
    });

  return objects;
};
